import React, { useState, useEffect } from 'react';
import './usertools-css/VitaminB12Calculator.css';
import '../../styles/components/ainutritionlandingpage.css';

const b12RdaTable = {
    '0-6m': { rda: 0.4, isAI: true },
    '7-12m': { rda: 0.5, isAI: true },
    '1-3y': { rda: 0.9 },
    '4-8y': { rda: 1.2 },
    '9-13y': { rda: 1.8 },
    '14+': { rda: 2.4 },
    'pregnant': { rda: 2.6 },
    'lactating': { rda: 2.8 },
    'adult50+': { rda: 2.4, note: "Recommend fortified sources or supplement" } // Base RDA is same, but absorption concerns
};

const riskFactorModifiers = {
    // Key: state variable name, Value: { absorptionReduction: decimal, name: string }
    'med_metformin': { absorptionReduction: 0.20, name: "Metformin Use" }, // Example reduction %
    'med_ppi_h2': { absorptionReduction: 0.30, name: "Acid-Reducing Meds (PPIs/H2 Blockers)" },
    'cond_gastric_surgery': { absorptionReduction: 0.60, name: "Gastric Surgery (e.g., Bypass)" }, // Higher impact
    'cond_crohns_celiac': { absorptionReduction: 0.40, name: "Crohn's or Celiac Disease" },
    'cond_pernicious_anemia': { absorptionReduction: 0.95, name: "Pernicious Anemia" }, // Severely impacts IF path
    'lifestyle_heavy_alcohol': { absorptionReduction: 0.15, name: "Heavy Alcohol Use" },
};

// --- Component ---
function VitaminB12Calculator() {
    // --- State for User Inputs ---
    const [age, setAge] = useState(''); // Use number input for age
    const [sex, setSex] = useState(''); // 'male', 'female'
    const [isPregnant, setIsPregnant] = useState(false);
    const [isLactating, setIsLactating] = useState(false);
    const [dietType, setDietType] = useState(''); // 'omnivore', 'lacto-ovo', 'pescatarian', 'vegan'
    const [consumesFortified, setConsumesFortified] = useState(false); // Simple check for fortified foods

    // Health Factors State
    const [healthFactors, setHealthFactors] = useState({
        med_metformin: false,
        med_ppi_h2: false, // Combined PPIs/H2 blockers
        cond_gastric_surgery: false,
        cond_crohns_celiac: false,
        cond_pernicious_anemia: false,
        lifestyle_heavy_alcohol: false,
        // Add others from blueprint if needed
    });

    // Supplement State
    const [takesSupplement, setTakesSupplement] = useState(false);
    const [supplementDoseMcg, setSupplementDoseMcg] = useState(''); // Dose in µg
    const [supplementFrequency, setSupplementFrequency] = useState(''); // 'daily', 'weekly', '3x_week', etc.

    // --- State for Results ---
    const [calculationResult, setCalculationResult] = useState(null); // { rda, rawIntake, absorptionFactor, effectiveIntake, gap, explanation, recommendations }
    const [showResults, setShowResults] = useState(false);

    // --- Input Handlers ---
    const handleHealthFactorChange = (event) => {
        const { name, checked } = event.target;
        setHealthFactors(prev => ({ ...prev, [name]: checked }));
    };

    // --- Derived State ---
    const ageNum = parseInt(age, 10);
    const isOver50 = ageNum >= 50;

    // Disable pregnancy/lactation based on sex and age
    useEffect(() => {
        if (sex === 'male' || ageNum < 14 || ageNum > 55) { // Adjust age range as needed
            setIsPregnant(false);
            setIsLactating(false);
        }
    }, [sex, ageNum]);

    // --- Calculation Logic ---
    const calculateB12Intake = (e) => {
        e.preventDefault(); // Prevent default form submission
        setShowResults(false);
        setCalculationResult(null);

        let explanation = [];
        let targetRda = 0;
        let rdaNote = "";

        // 1. Determine RDA
        if (!isNaN(ageNum)) {
            if (isLactating) {
                targetRda = b12RdaTable['lactating'].rda;
                explanation.push(`Target RDA (Lactating): ${targetRda} µg/day.`);
            } else if (isPregnant) {
                targetRda = b12RdaTable['pregnant'].rda;
                explanation.push(`Target RDA (Pregnant): ${targetRda} µg/day.`);
            } else if (ageNum >= 0 && ageNum < 0.5) {
                targetRda = b12RdaTable['0-6m'].rda;
                explanation.push(`Target AI (0-6 months): ${targetRda} µg/day.`);
            } else if (ageNum >= 0.5 && ageNum < 1) {
                targetRda = b12RdaTable['7-12m'].rda;
                explanation.push(`Target AI (7-12 months): ${targetRda} µg/day.`);
            } else if (ageNum >= 1 && ageNum <= 3) {
                targetRda = b12RdaTable['1-3y'].rda;
                explanation.push(`Target RDA (1-3 years): ${targetRda} µg/day.`);
            } else if (ageNum >= 4 && ageNum <= 8) {
                targetRda = b12RdaTable['4-8y'].rda;
                explanation.push(`Target RDA (4-8 years): ${targetRda} µg/day.`);
            } else if (ageNum >= 9 && ageNum <= 13) {
                targetRda = b12RdaTable['9-13y'].rda;
                explanation.push(`Target RDA (9-13 years): ${targetRda} µg/day.`);
            } else if (ageNum >= 14) {
                targetRda = b12RdaTable['14+'].rda;
                explanation.push(`Target RDA (14+ years): ${targetRda} µg/day.`);
                if (isOver50) {
                    rdaNote = b12RdaTable['adult50+'].note || "";
                    explanation.push(`Note for 50+: ${rdaNote}.`);
                }
            }
        } else {
            explanation.push("Please enter a valid age.");
            targetRda = b12RdaTable['14+'].rda; // Default fallback
        }


        // 2. Estimate Food Intake (Simplified)
        let foodIntake = 0;
        switch (dietType) {
            case 'omnivore': foodIntake = 5.0; explanation.push("Estimated baseline food intake (Omnivore): ~5.0 µg/day."); break;
            case 'pescatarian': foodIntake = 4.0; explanation.push("Estimated baseline food intake (Pescatarian): ~4.0 µg/day."); break;
            case 'lacto-ovo': foodIntake = 1.5; explanation.push("Estimated baseline food intake (Lacto-Ovo Vegetarian): ~1.5 µg/day."); break;
            case 'vegan': foodIntake = 0.2; explanation.push("Estimated baseline food intake (Vegan - assumed minimal unfortified): ~0.2 µg/day."); break;
            default: foodIntake = 0; explanation.push("Select a diet type to estimate food intake."); break;
        }
        if (consumesFortified) {
            foodIntake += 3.0; // Rough average addition
            explanation.push("+3.0 µg/day added for regular fortified food consumption (e.g., plant milks, cereals, nutritional yeast).");
        }

        // 3. Calculate Supplement Intake
        let supplementIntake = 0;
        const dose = parseFloat(supplementDoseMcg);
        if (takesSupplement && !isNaN(dose) && dose > 0 && supplementFrequency) {
            let dailyFactor = 0;
            switch (supplementFrequency) {
                case 'daily': dailyFactor = 1; break;
                case '3x_week': dailyFactor = 3 / 7; break;
                case 'weekly': dailyFactor = 1 / 7; break;
                case 'monthly': dailyFactor = 1 / 30; break; // Less common for oral B12 but possible
                default: dailyFactor = 0; break;
            }
            supplementIntake = dose * dailyFactor;
            explanation.push(`Supplement contribution: ${dose} µg taken ${supplementFrequency} ≈ ${supplementIntake.toFixed(1)} µg/day average.`);
        }

        const rawIntake = foodIntake + supplementIntake;
        explanation.push(`Estimated total raw intake (Food + Supplements): ${rawIntake.toFixed(1)} µg/day.`);

        // 4. Calculate Absorption Factor
        let absorptionFactor = 1.0;
        let activeRiskFactors = [];
        Object.keys(healthFactors).forEach(key => {
            if (healthFactors[key] && riskFactorModifiers[key]) {
                const modifier = riskFactorModifiers[key];
                absorptionFactor -= modifier.absorptionReduction;
                explanation.push(`Absorption reduced by ${modifier.absorptionReduction * 100}% due to ${modifier.name}.`);
                activeRiskFactors.push(modifier.name);
            }
        });
        // Add lower cap for absorption
        absorptionFactor = Math.max(0.05, absorptionFactor); // Minimum 5% absorption
        explanation.push(`Final absorption factor: ${(absorptionFactor * 100).toFixed(0)}%.`);

        // 5. Calculate Effective Intake
        const effectiveIntake = rawIntake * absorptionFactor;
        explanation.push(`Estimated effective intake (after absorption): ${effectiveIntake.toFixed(1)} µg/day.`);

        // 6. Calculate Gap
        const gap = targetRda - effectiveIntake;
        explanation.push(`Gap compared to RDA: ${gap > 0 ? `${gap.toFixed(1)} µg/day deficit` : `${Math.abs(gap).toFixed(1)} µg/day surplus (or adequate)`}.`);

        // 7. Generate Recommendations (Simplified Placeholder)
        let recommendations = [];
        if (gap > 0) {
            recommendations.push("Recommendation: Your estimated intake may be below the recommended level.");
            if (gap > 0.5 && gap <= 5 && !takesSupplement) { // Example logic
                recommendations.push("Consider adding B12-fortified foods daily or a low-dose supplement (e.g., 25-100 µg/day).");
            } else if (gap > 5 && !takesSupplement) {
                recommendations.push("A higher dose supplement (e.g., 1000 µg several times a week or daily depending on severity and absorption) might be needed. Consult a healthcare provider.");
            } else if (takesSupplement && gap > 1) {
                recommendations.push("Your current supplement might not be sufficient, or absorption issues may be significant. Discuss adjusting dose/frequency or investigating absorption with your provider.");
            }
            recommendations.push("Focus on high-B12 foods suitable for your diet (see below).");
        } else {
            recommendations.push("Recommendation: Your estimated intake appears adequate based on inputs.");
        }
        if (activeRiskFactors.length > 0) {
            recommendations.push(`Important: Factors like [${activeRiskFactors.join(', ')}] significantly impact B12 absorption. Regular monitoring via blood tests (Serum B12, MMA, Homocysteine) is advisable.`);
        }
        if (isOver50) {
            recommendations.push("Adults over 50 often have reduced absorption from food; relying on fortified foods or supplements is generally recommended.");
        }
        if (dietType === 'vegan' || dietType === 'lacto-ovo') {
            recommendations.push(`For ${dietType} diets, reliable B12 sources (fortified foods or supplements) are essential.`);
        }
        recommendations.push("Consult your healthcare provider to discuss these estimates, potential testing, and personalized advice.");


        // Set results after a short delay
        setTimeout(() => {
            setCalculationResult({
                rda: targetRda,
                rawIntake: rawIntake.toFixed(1),
                absorptionFactor: (absorptionFactor * 100).toFixed(0),
                effectiveIntake: effectiveIntake.toFixed(1),
                gap: gap.toFixed(1),
                explanation: explanation.join('\n'),
                recommendations: recommendations,
                rdaNote: rdaNote
            });
            setShowResults(true);
        }, 300);
    };

    // --- JSX Structure ---
    return (
        // Add a specific class for B12 styling
        <div className="vitaminB12Calculator">
            <h1>
                <span>
                    <span role="img" aria-label="vitamin b12 calculator">
                        🧬
                    </span>{' '}
                    Vitamin B12 Intake Calculator
                </span>

                <span className="ai-promo-inline">
                    <strong>Unlock Smarter Eating:</strong>{' '}
                    Get a complete breakdown of vitamins, minerals, and fats plus a personalized
                    weight-loss plan or nutrition tailored to your training with a single{' '}
                    <a
                        href="https://purehealthnexus.com/ai-nutrition-vitamins-minerals"
                        className="ai-promo-link"
                    >
                        AI scan
                    </a>.
                </span>
            </h1>

            <p className="important-note">
                <strong>Disclaimer:</strong> This tool provides an ESTIMATE based on general dietary patterns and common risk factors. It is NOT a substitute for professional medical advice, diagnosis, or treatment. Food intake estimations are highly simplified. Always seek the advice of your physician or other qualified health provider regarding your B12 status and needs, especially if you have symptoms or risk factors. Lab testing (Serum B12, MMA) is the definitive way to assess status.
            </p>

            {/* --- Input Form --- */}
            <form onSubmit={calculateB12Intake}>
                <h2>🧾 Your Information</h2>

                {/* Demographics */}
                <div className="form-section">
                    <label>
                        Age (Years):
                        <input type="number" value={age} onChange={(e) => setAge(e.target.value)} required min="0" max="120" placeholder="e.g., 35" />
                    </label>
                    <label>
                        Sex Assigned at Birth:
                        <select value={sex} onChange={(e) => setSex(e.target.value)} required>
                            <option value="">-- Select --</option>
                            <option value="female">Female</option>
                            <option value="male">Male</option>
                            {/* Add 'Other' if needed, consider impact on pregnancy/lactation logic */}
                        </select>
                    </label>
                    {sex === 'female' && ageNum >= 14 && ageNum <= 55 && ( // Conditional rendering
                        <>
                            <label className="checkbox-label">
                                <input type="checkbox" checked={isPregnant} onChange={(e) => setIsPregnant(e.target.checked)} disabled={isLactating} />
                                Currently Pregnant
                            </label>
                            <label className="checkbox-label">
                                <input type="checkbox" checked={isLactating} onChange={(e) => setIsLactating(e.target.checked)} disabled={isPregnant} />
                                Currently Lactating
                            </label>
                        </>
                    )}
                    {isOver50 && <p className='info-flag'>👤 Age 50+ selected (may affect absorption)</p>}
                </div>

                {/* Diet and Basic Intake */}
                <div className="form-section">
                    <h3>🍽️ Dietary Habits</h3>
                    <label>
                        Primary Diet Pattern:
                        <select value={dietType} onChange={(e) => setDietType(e.target.value)} required>
                            <option value="">-- Select --</option>
                            <option value="omnivore">Omnivore (Includes meat, fish, dairy, eggs)</option>
                            <option value="pescatarian">Pescatarian (Includes fish, dairy, eggs; no other meat)</option>
                            <option value="lacto-ovo">Lacto-Ovo Vegetarian (Includes dairy, eggs; no meat/fish)</option>
                            <option value="vegan">Vegan (No animal products)</option>
                        </select>
                    </label>
                    <label className="checkbox-label">
                        <input type="checkbox" checked={consumesFortified} onChange={(e) => setConsumesFortified(e.target.checked)} />
                        Regularly consume B12-fortified foods? (e.g., Plant milks, cereals, nutritional yeast - check labels)
                    </label>
                </div>

                {/* Health Factors Affecting Absorption */}
                <div className="form-section">
                    <h3>🩺 Health Factors Affecting B12 Absorption</h3>
                    <fieldset>
                        <legend>Check any that apply:</legend>
                        {Object.entries(riskFactorModifiers).map(([key, factor]) => (
                            <label key={key} className="checkbox-label indented">
                                <input
                                    type="checkbox"
                                    name={key}
                                    checked={healthFactors[key]}
                                    onChange={handleHealthFactorChange}
                                />
                                {factor.name}
                            </label>
                        ))}
                    </fieldset>
                </div>

                {/* Supplement Intake */}
                <div className="form-section">
                    <h3>💊 Vitamin B12 Supplement Use</h3>
                    <label className="checkbox-label">
                        <input type="checkbox" checked={takesSupplement} onChange={(e) => setTakesSupplement(e.target.checked)} />
                        Do you currently take a Vitamin B12 supplement?
                    </label>
                    {takesSupplement && (
                        <div className='supplement-details'>
                            <label>
                                Dose per intake (mcg / µg):
                                <input type="number" value={supplementDoseMcg} onChange={(e) => setSupplementDoseMcg(e.target.value)} required={takesSupplement} min="1" placeholder="e.g., 1000" />
                            </label>
                            <label>
                                Frequency:
                                <select value={supplementFrequency} onChange={(e) => setSupplementFrequency(e.target.value)} required={takesSupplement}>
                                    <option value="">-- Select --</option>
                                    <option value="daily">Daily</option>
                                    <option value="3x_week">3 times per week</option>
                                    <option value="weekly">Once per week</option>
                                    <option value="monthly">Once per month</option>
                                    {/* Add other frequencies if needed */}
                                </select>
                            </label>
                        </div>
                    )}
                </div>

                <button type="submit">Calculate B12 Intake</button>
            </form>

            {/* --- Results Display --- */}
            {showResults && calculationResult && (
                <div className="results-section">
                    <h2>📊 Estimated Vitamin B12 Status</h2>
                    <div className="results-grid">
                        <div className="result-item">
                            <span className="result-label">Target RDA/AI:</span>
                            <span className="result-value">{calculationResult.rda} µg/day</span>
                            {calculationResult.rdaNote && <small className='result-note'>{calculationResult.rdaNote}</small>}
                        </div>
                        <div className="result-item">
                            <span className="result-label">Est. Raw Intake:</span>
                            <span className="result-value">{calculationResult.rawIntake} µg/day</span>
                            <small className='result-note'>(Food + Supplements, before absorption)</small>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Est. Absorption Factor:</span>
                            <span className="result-value">{calculationResult.absorptionFactor}%</span>
                            <small className='result-note'>(Affected by health factors)</small>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Est. Effective Intake:</span>
                            <span className="result-value significant">{calculationResult.effectiveIntake} µg/day</span>
                            <small className='result-note'>(Amount likely absorbed)</small>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Intake Status:</span>
                            <span className={`result-value status-${calculationResult.gap > 0.5 ? 'low' : calculationResult.gap > 0 ? 'borderline' : 'adequate'}`}>
                                {calculationResult.gap > 0.5 ? `Deficit (~${calculationResult.gap} µg)` : calculationResult.gap > 0 ? `Borderline (~${calculationResult.gap} µg deficit)` : 'Likely Adequate'}
                            </span>
                        </div>
                    </div>

                    <h3>📝 Explanation & Recommendations</h3>
                    <pre className="explanation-box">{calculationResult.explanation}</pre>

                    <div className='recommendations-box'>
                        <h4>Recommendations Summary:</h4>
                        <ul>
                            {calculationResult.recommendations.map((rec, index) => (
                                <li key={index}>{rec}</li>
                            ))}
                        </ul>
                        {/* Placeholder for Food/Supplement Links */}
                        <p>
                            <i>Detailed food suggestions and specific supplement recommendations based on the gap would appear here in a full implementation.</i>
                        </p>
                        <a href="#b12-education" className="scroll-link">Learn more about Vitamin B12 below</a>
                    </div>

                    <p className="important-note">
                        <strong>Crucial Reminder:</strong> This estimate is based on averages and self-reported data. Individual absorption and needs vary greatly. This tool CANNOT diagnose deficiency. If you suspect a B12 deficiency based on symptoms (see below) or risk factors, consult a healthcare professional for proper testing and guidance.
                    </p>
                </div>
            )}

            {/* --- Educational Content (Needs B12 specific info) --- */}
            <div id="b12-education" className="educational-section">
                <h2>Understanding Vitamin B12</h2>

                {/* Replace ALL content below with B12 specific info from your blueprint */}
                <div className="content-block">
                    <h3>What is Vitamin B12 and Why is it Important?</h3>
                    <p>
                        Vitamin B12 (cobalamin) is a water-soluble vitamin crucial for nerve function, red blood cell formation, and DNA synthesis. It acts as a cofactor for key enzymes like methionine synthase (important for managing homocysteine levels) and L-methylmalonyl-CoA mutase (involved in fatty acid metabolism).
                    </p>
                    {/* Link to relevant article */}
                    <a href="https://purehealthnexus.com/lionsmane" target="_blank" rel="noopener noreferrer" className="educational-link">
                        Learn More: Lion's Mane Mushroom Benefits
                    </a>
                </div>

                <div className="content-block">
                    <h3>How is B12 Absorbed?</h3>
                    <ul>
                        <li><strong>Stomach Acid:</strong> Frees B12 from food proteins.</li>
                        <li><strong>Intrinsic Factor (IF):</strong> A protein made by stomach cells (parietal cells) that binds to B12.</li>
                        <li><strong>Small Intestine:</strong> The IF-B12 complex travels to the final section (terminal ileum) where it's absorbed into the bloodstream.</li>
                        <li><strong>Factors Impeding Absorption:</strong> Low stomach acid (common in older adults or due to acid-reducing meds), lack of IF (pernicious anemia), damage to the stomach or ileum (surgery, Crohn's, Celiac disease).</li>
                    </ul>
                </div>

                <div className="content-block">
                    <h3>Recommended Intakes & Sources</h3>
                    <ul>
                        <li><strong>RDA:</strong> Varies by age (see calculator results). Adults generally need 2.4 µg/day, with higher needs during pregnancy (2.6 µg) and lactation (2.8 µg).</li>
                        <li><strong>Natural Sources:</strong> Primarily animal products – liver, clams, beef, fish (trout, salmon, tuna), eggs, dairy.</li>
                        <li><strong>Fortified Sources:</strong> Essential for vegans/vegetarians. Includes many breakfast cereals, plant-based milks (check labels!), nutritional yeast, and some meat substitutes. B12 amounts vary significantly by brand.</li>
                        <li><strong>Supplements:</strong> Available as cyanocobalamin (most common, stable) or methylcobalamin (active form). Forms include tablets, sublingual lozenges, sprays, and injections (hydroxocobalamin or cyanocobalamin, often Rx).</li>
                        <li><strong>Body Stores:</strong> The liver stores a significant amount (2-5 mg). Deficiency can take years to develop (2-4 years) if intake stops completely, as daily losses are small (~0.1%).</li>
                        <li><strong>Toxicity:</strong> No Tolerable Upper Intake Level (UL) is set. Excess B12 is typically excreted in urine; it's considered non-toxic even at high oral doses.</li>
                    </ul>
                    {/* Link to relevant article */}
                    <a href="https://purehealthnexus.com/what-vitamins-should-i-take" target="_blank" rel="noopener noreferrer" className="educational-link">
                        What vitamins should I take? Understanding Your Vitamin Needs: A Guide
                    </a>
                </div>

                <div className="content-block">
                    <h3>Who is at Risk for Deficiency?</h3>
                    <ul>
                        <li>Vegetarians and especially Vegans (if not supplementing/using fortified foods reliably).</li>
                        <li>Adults over 50 (due to reduced stomach acid/IF production).</li>
                        <li>Individuals with GI disorders (Crohn's, Celiac, atrophic gastritis).</li>
                        <li>People who have had gastric or ileal resection surgery (e.g., bariatric surgery).</li>
                        <li>Individuals with Pernicious Anemia (autoimmune condition destroying IF-producing cells).</li>
                        <li>Long-term users of Metformin or acid-suppressing medications (PPIs, H2 blockers).</li>
                        <li>Individuals with chronic heavy alcohol consumption.</li>
                        <li>Infants breastfed by mothers with B12 deficiency.</li>
                    </ul>
                </div>

                <div className="content-block">
                    <h3>Symptoms of Deficiency</h3>
                    <p>Can be diverse and develop gradually. May include:</p>
                    <ul>
                        <li>Tingling, numbness, or burning in hands, legs, or feet (paresthesia).</li>
                        <li>Fatigue and weakness.</li>
                        <li>Anemia (megaloblastic anemia - large, immature red blood cells).</li>
                        <li>Difficulty walking, balance problems.</li>
                        <li>Sore, red tongue (glossitis), mouth ulcers.</li>
                        <li>Cognitive difficulties, memory loss, confusion ('brain fog').</li>
                        <li>Mood changes, depression, irritability.</li>
                        <li>Pale or jaundiced skin.</li>
                        <li>Vision disturbances (rare).</li>
                    </ul>
                    <p>Neurological symptoms can sometimes occur even without anemia and may be irreversible if deficiency is prolonged.</p>
                </div>

                <div className="content-block">
                    <h3>Testing for B12 Status</h3>
                    <ul>
                        <li><strong>Serum B12:</strong> Most common test. Levels &lt; 148 pmol/L (or &lt; 200 pg/mL) usually indicate deficiency, but levels in the lower end of normal ('grey zone', e.g., 148-300 pmol/L) may still be insufficient for some, especially if symptoms are present.</li>
                        <li><strong>Methylmalonic Acid (MMA):</strong> A more sensitive marker of functional B12 deficiency. Elevated MMA (in serum or urine) often indicates a B12 tissue shortage, even if serum B12 is borderline.</li>
                        <li><strong>Homocysteine:</strong> Also increases with B12 deficiency (and folate/B6 deficiency). Less specific than MMA.</li>
                        <li><strong>Complete Blood Count (CBC):</strong> May show megaloblastic anemia (high MCV).</li>
                    </ul>
                    <p>Consult your doctor about which tests are appropriate for you.</p>
                </div>
                {/* Sources Section */}
                <p className="sources">
                    NIH Office of Dietary Supplements – “Vitamin B12 Fact Sheet for Health Professionals.” Office of Dietary Supplements (ODS).<br />
                    NIH Office of Dietary Supplements – “Vitamin B12 Fact Sheet for Consumers.” Office of Dietary Supplements (ODS).<br />
                    USDA FoodData Central – nutrient data for B12‑containing foods. FoodData Central.<br />
                    StatPearls/NCBI Bookshelf – Langan RC, Zawistoski K. “Vitamin B12 Deficiency.” NCBI.<br />
                    NCBI Bookshelf – Stabler SP, Allen RH. “Vitamin B12 (Cobalamin).” NCBI.<br />
                    Scientific Advisory Committee on Nutrition (UK) – “Vitamin B12 and Health” report. GOV.UK.<br />
                    American Diabetes Association – Standards of Care in Diabetes 2025, Section 9 (metformin & B12 monitoring). Diabetes Journals.<br />
                    American Society for Metabolic and Bariatric Surgery – “Nutritional Guidelines in Bariatric Surgery” (2016 update). asmbs.org.
                </p>

            </div>
        </div>
    );
}

export default VitaminB12Calculator;